import { useCallback, useMemo } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import { WORKDAY_HIRE_FLAG, WORKDAY_INTEGRATION_TYPE } from './constants';
import { WORKDAY_POSITION_FLAG } from '../workdayPosition/constants';

/**
 * Decides whether Workday's own hire form replaces Kombo's employee form.
 *
 * Three conditions, and the position flag is one of them. `create_hire` refuses
 * an application with no position, so the custom form without the position flow
 * is a dead end: the recruiter fills it in and gets
 * `err_workday_position_missing` with no way to create the position. Anything
 * short of all three falls back to the Kombo form, unchanged.
 */
export const useWorkdayHire = () => {
  const flags = useKFlags();
  const isFlagOn = !!flags[WORKDAY_HIRE_FLAG] && !!flags[WORKDAY_POSITION_FLAG];

  // Both memoised deliberately: `usesWorkdayHire` sits in three `useCallback`
  // dependency arrays in `useHrisIntegrations`, and one of those
  // (`handlePushToHRIS`) is a prop on the memoised `ApplicationActions`. A new
  // identity each render silently defeats all four.
  const usesWorkdayHire = useCallback(
    (integrationType?: string) =>
      isFlagOn && integrationType === WORKDAY_INTEGRATION_TYPE,
    [isFlagOn]
  );

  return useMemo(
    () => ({ isFlagOn, usesWorkdayHire }),
    [isFlagOn, usesWorkdayHire]
  );
};
